import React, { useEffect, useState } from "react";
import { InfoCircle } from "iconsax-reactjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { handleUpdateBusinessFinancialInformation } from "api/onboarding";
import EmailSuggestionButton from "component/auth/onboarding/EmailSuggestionButton";
import OnboardingLayout from "component/auth/OnboardingLayout";
import Dropdown from "component/form/dropDown";
import FileUploadTile from "component/form/FileUploadTile";
import InfoTooltip from "component/InfoTooltip";
import Loader from "component/loader";
import WalletDetailsModal from "component/wallet/WalletDetailsModal";
import WalletReadyModal from "component/wallet/WalletReadyModal";
import { formatMoney } from "utilities/formatMoney";

const steps = [{ title: "Control person" }, { title: "Additional information" }];
const LOCKED_FIELD_MESSAGE = "This information was previously submitted and can’t be changed.";

const LockedField = ({ locked, className = "", children }) => (
    <div
        title={locked ? LOCKED_FIELD_MESSAGE : undefined}
        aria-disabled={locked || undefined}
        className={`${className} ${
            locked
                ? "cursor-not-allowed [&_*]:!cursor-not-allowed [&_input]:!bg-[#F8FAFC] [&_input]:!text-[#98A2B3] [&_input]:[-webkit-text-fill-color:#98A2B3] [&_textarea]:!bg-[#F8FAFC] [&_textarea]:!text-[#98A2B3] [&_textarea]:[-webkit-text-fill-color:#98A2B3]"
                : ""
        }`}
    >
        {children}
    </div>
);

const VOLUME_CURRENCY_OPTIONS = [
    { value: "NGN", label: "NGN" },
    { value: "USD", label: "USD" },
];

const SOURCE_OF_FUNDS_OPTIONS = [
    { value: "salary_wages", label: "Salary/wages" },
    { value: "business_revenue", label: "Business revenue" },
    { value: "investment", label: "Investment" },
    { value: "sales_of_assets", label: "Sales of assets" },
    { value: "loan_credit_line", label: "Loan / Credit line" },
    { value: "other_sources", label: "Other sources" },
];

const SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE = {
    salary_wages: [
        { value: "payslip", label: "Payslips" },
        { value: "tax_returns", label: "Tax returns" },
        { value: "employment_contract", label: "Employment contract" },
        { value: "bank_statement", label: "Bank statement" },
    ],
    business_revenue: [
        { value: "invoice", label: "Invoice" },
        { value: "signed_contract", label: "Signed contract" },
        { value: "bank_statement", label: "Bank statement" },
    ],
    investment: [
        { value: "investment_agreement", label: "Investment agreement" },
        { value: "grant_letter", label: "Grant letter" },
        { value: "bank_transfer_receipt", label: "Bank transfer receipt" },
    ],
    sales_of_assets: [
        { value: "sale_agreement", label: "Sale agreement" },
        { value: "title_transfer_documents", label: "Title transfer documents" },
        { value: "bank_statement", label: "Bank statement" },
    ],
    loan_credit_line: [
        { value: "loan_agreement", label: "Loan agreement" },
        { value: "proof_of_disbursement", label: "Proof of disbursement" },
        { value: "lender_details", label: "Lender details" },
    ],
    other_sources: [
        { value: "bank_statement", label: "Bank statement" },
        { value: "supporting_document", label: "Supporting document" },
        { value: "other", label: "Other" },
    ],
};

const WEBSITE_PATTERN = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getUploadedUrl = (doc) =>
    (typeof doc === "string" && doc) ||
    doc?.url ||
    doc?.url?.link ||
    doc?.link ||
    doc?.document?.url ||
    doc?.document?.link ||
    doc?.data?.url ||
    doc?.data?.url?.link ||
    doc?.data?.link ||
    "";

const normalizeUrl = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const hasSubmittedValue = (value) =>
    value !== undefined && value !== null && (typeof value !== "string" || value.trim() !== "");

const normalizeOptionValue = (value, options) => {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s/-]+/g, "_");

    return (
        options.find((option) => {
            const optionValue = String(option.value).toLowerCase();
            const optionLabel = String(option.label)
                .toLowerCase()
                .replace(/[\s/-]+/g, "_");
            return optionValue === normalized || optionLabel === normalized;
        })?.value || ""
    );
};

export default function BusinessInformation({ currentStep = 1, onNext, onBack }) {
    const navigate = useNavigate();
  const dispatch = useDispatch();
  const { business, businessid, users, kycStatus } = useSelector((state) => state);
    const businessId = businessid || business?._id;
    const accountEmail = users?.email || "";

    const [volumeCurrency, setVolumeCurrency] = useState("NGN");
    const [averageMonthlyVolume, setAverageMonthlyVolume] = useState("");
    const [businessDescription, setBusinessDescription] = useState("");
    const [businessEmail, setBusinessEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [sourceOfFunds, setSourceOfFunds] = useState("");
    const [sourceDocumentType, setSourceDocumentType] = useState("");
    const [sourceOfFundsDoc, setSourceOfFundsDoc] = useState(null);
    const [lockedFields, setLockedFields] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [openWalletCode, setOpenWalletCode] = useState(null);
    const [readyWallet, setReadyWallet] = useState(null);

    useEffect(() => {
        const existingDescription = business?.businessDescription || business?.description || "";
        const existingEmail = business?.email || business?.businessEmail || "";
        const existingWebsite = business?.website || business?.businessWebsite || "";
        const monthlyVolume = business?.monthlyTransactionVolume || {};
        const sourceDocument = business?.sourceOfFund || business?.sourceOfFunds || null;
        const sourceValue = normalizeOptionValue(
            sourceDocument?.type || sourceDocument?.source,
            SOURCE_OF_FUNDS_OPTIONS
        );
        const documentOptions = SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[sourceValue] || [];
        const documentValue = normalizeOptionValue(
            sourceDocument?.documentType || sourceDocument?.name,
            documentOptions
        );
        const sourceDocumentUrl = getUploadedUrl(sourceDocument);
        const sourceDocumentKey =
            sourceDocument?.key || sourceDocument?.document?.key || sourceDocument?.fileId || "";

        if (hasSubmittedValue(monthlyVolume?.currency)) {
            setVolumeCurrency(String(monthlyVolume.currency).toUpperCase());
        }
        if (hasSubmittedValue(monthlyVolume?.amount)) {
            setAverageMonthlyVolume(formatMoney(String(monthlyVolume.amount)));
        }
        if (existingDescription) setBusinessDescription(existingDescription);
        if (existingEmail) setBusinessEmail(existingEmail);
        if (existingWebsite) setWebsite(existingWebsite);
        if (sourceValue) setSourceOfFunds(sourceValue);
        if (documentValue) setSourceDocumentType(documentValue);
        if (sourceDocumentUrl || sourceDocumentKey) {
            setSourceOfFundsDoc({
                name:
                    sourceDocument?.fileName ||
                    sourceDocument?.name ||
                    sourceDocument?.key ||
                    sourceDocument?.document?.key ||
                    "Source of funds document",
                url: sourceDocumentUrl,
                key: sourceDocumentKey,
                fileId: sourceDocument?.fileId || sourceDocumentKey,
                rawType: sourceDocument?.type || sourceDocument?.source || "",
                rawName: sourceDocument?.documentType || sourceDocument?.name || "",
            });
        }

        const submittedFields = {
            volumeCurrency: hasSubmittedValue(monthlyVolume?.currency),
            averageMonthlyVolume: hasSubmittedValue(monthlyVolume?.amount),
            businessDescription: hasSubmittedValue(existingDescription),
            businessEmail: hasSubmittedValue(existingEmail),
            website: hasSubmittedValue(existingWebsite),
            sourceOfFunds: hasSubmittedValue(sourceValue),
            sourceDocumentType: hasSubmittedValue(documentValue),
            sourceOfFundsDoc: Boolean(sourceDocumentUrl || sourceDocumentKey),
        };
      const locked = Object.keys(submittedFields).filter((field) => submittedFields[field]);
      setLockedFields(locked);
    }, [business]);

  const isFieldLocked = (field) => lockedFields.includes(field);

    const sourceDocumentTypeOptions =
        SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[sourceOfFunds] || [];
    const selectedSource = SOURCE_OF_FUNDS_OPTIONS.find((option) => option.value === sourceOfFunds);
    const selectedDocumentType = sourceDocumentTypeOptions.find(
        (option) => option.value === sourceDocumentType
    );
    const cleanVolume = averageMonthlyVolume.replace(/,/g, "").trim();
    const cleanBusinessDescription = businessDescription.trim();
    const cleanBusinessEmail = businessEmail.trim();
    const businessEmailIsValid = EMAIL_PATTERN.test(cleanBusinessEmail);
    const cleanWebsite = website.trim();
    const websiteIsValid = Boolean(cleanWebsite && WEBSITE_PATTERN.test(cleanWebsite));
    const sourceDocumentUrl = getUploadedUrl(sourceOfFundsDoc);
    const hasSourceDocument = Boolean(
        sourceDocumentUrl || sourceOfFundsDoc?.key || sourceOfFundsDoc?.fileId
    );
    const hasExistingSourceDocument = isFieldLocked("sourceOfFundsDoc") && hasSourceDocument;
    const sourceOfFundsComplete =
        hasExistingSourceDocument ||
        Boolean(sourceOfFunds && sourceDocumentType && sourceDocumentUrl);
    const canSubmit = Boolean(
        businessId &&
        cleanVolume &&
        cleanBusinessDescription &&
        businessEmailIsValid &&
        websiteIsValid &&
        sourceOfFundsComplete
    );

    const handleSubmit = async () => {
        if (!canSubmit || submitting) return;

        const body = {
            wallets: [{ currency: "USD", flag: "🇺🇸" }],
        };

        if (!isFieldLocked("averageMonthlyVolume") || !isFieldLocked("volumeCurrency")) {
            body.monthlyTransactionVolume = {
                currency: volumeCurrency,
                amount: cleanVolume,
            };
        }

        if (!isFieldLocked("businessDescription")) {
            body.businessDescription = cleanBusinessDescription;
        }

        if (!isFieldLocked("businessEmail")) {
            body.email = cleanBusinessEmail;
        }

        if (!isFieldLocked("website")) {
            body.hasWebsite = true;
            body.businessWebsite = normalizeUrl(website);
        }

        if (
            !hasExistingSourceDocument &&
            (!isFieldLocked("sourceOfFunds") ||
                !isFieldLocked("sourceDocumentType") ||
                !isFieldLocked("sourceOfFundsDoc"))
        ) {
            body.sourceOfFund = {
                type: selectedSource?.label || sourceOfFundsDoc?.rawType || sourceOfFunds,
                name:
                    selectedDocumentType?.label ||
                    sourceOfFundsDoc?.rawName ||
                    sourceDocumentType,
                url: sourceDocumentUrl,
                key: sourceOfFundsDoc?.key || sourceOfFundsDoc?.fileId || "",
            };
        }

        try {
            setSubmitting(true);
            setErrorMessage("");
            await handleUpdateBusinessFinancialInformation(businessId, body);
            sessionStorage.removeItem("postKycOpenCurrency");
          dispatch({ type: "KYC_STATUS", payload: "Completed" });

        } catch (error) {
            setErrorMessage(error?.message || "Unable to save the additional information.");
        } finally {
            setSubmitting(false);
        }
    };

  useEffect(() => {
    if (kycStatus === 'Completed') {
      setOpenWalletCode("USD");
    }
  }, [kycStatus]);

    return (
      <OnboardingLayout title="Add USD wallet" isUpdating={kycStatus.length ? true : false} steps={steps} currentStep={currentStep} showSkip={kycStatus.length ? false : true}>
            <div className="mx-auto w-full max-w-[760px]">
                <h1 className="mb-8 aku-heading-h2-medium text-[#101828]">
                    Additional information
                </h1>

                {errorMessage && <p className="mb-4 text-sm text-red-600">{errorMessage}</p>}

                <div className="space-y-6">
                    <div>
                        <p className="mb-1 aku-body-small-medium text-[#101828]">
                            What&apos;s your average transaction volume per month?
                        </p>
                        <div className="flex h-[56px] overflow-visible rounded-lg bg-[#f1f5f9]">
                            <LockedField
                                locked={isFieldLocked("volumeCurrency")}
                                className="w-[104px] shrink-0"
                            >
                                <Dropdown
                                    value={volumeCurrency}
                                    options={VOLUME_CURRENCY_OPTIONS}
                                    onChange={setVolumeCurrency}
                                    className="!mt-0 !rounded-r-none"
                                    dropdownClassName="!min-w-[104px]"
                                    readOnly={isFieldLocked("volumeCurrency")}
                                />
                            </LockedField>
                            <LockedField
                                locked={isFieldLocked("averageMonthlyVolume")}
                                className="min-w-0 flex-1"
                            >
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={averageMonthlyVolume}
                                    onChange={(event) => {
                                        const numericValue = event.target.value.replace(/\D/g, "");
                                        setAverageMonthlyVolume(
                                            numericValue ? formatMoney(numericValue) : ""
                                        );
                                    }}
                                    disabled={isFieldLocked("averageMonthlyVolume")}
                                    placeholder="e.g. 100,000"
                                    className="h-full w-full bg-transparent px-4 text-[#101828] outline-none placeholder:text-[#7A80AE]"
                                />
                            </LockedField>
                            <span className="flex items-center px-5 font-medium text-[#383B56]">
                                monthly
                            </span>
                        </div>
                    </div>

                    <div>
                        <p className="mb-1 aku-body-small-medium text-[#101828]">
                            Business description
                        </p>
                        <LockedField locked={isFieldLocked("businessDescription")}>
                            <textarea
                                value={businessDescription}
                                onChange={(event) => setBusinessDescription(event.target.value)}
                                disabled={isFieldLocked("businessDescription")}
                                placeholder="Describe your business"
                                rows={4}
                                className="min-h-[112px] w-full resize-none rounded-lg bg-[#f1f5f9] px-4 py-4 text-[#101828] outline-none placeholder:text-[#7A80AE]"
                            />
                        </LockedField>
                    </div>

                    <div>
                        <p className="aku-body-small-medium mb-1 text-[#101828]">Business email</p>
                        <LockedField locked={isFieldLocked("businessEmail")}>
                            <input
                                type="email"
                                value={businessEmail}
                                onChange={(event) => setBusinessEmail(event.target.value)}
                                disabled={isFieldLocked("businessEmail")}
                                placeholder="Enter business email address"
                                className={`h-[56px] w-full rounded-lg bg-[#f1f5f9] px-4 text-[#101828] outline-none placeholder:text-[#7A80AE] ${
                                    cleanBusinessEmail && !businessEmailIsValid
                                        ? "ring-1 ring-red-500"
                                        : ""
                                }`}
                            />
                        </LockedField>
                        {cleanBusinessEmail && !businessEmailIsValid && (
                            <p className="mt-1 text-sm text-red-600">
                                Enter a valid business email address.
                            </p>
                        )}
                        {!isFieldLocked("businessEmail") &&
                            accountEmail &&
                            cleanBusinessEmail !== accountEmail && (
                                <EmailSuggestionButton
                                    email={accountEmail}
                                    onClick={() => setBusinessEmail(accountEmail)}
                                />
                            )}
                    </div>

                    <div>
                        <p className="mb-1 aku-body-small-medium text-[#101828]">
                            Business website
                        </p>
                        <LockedField locked={isFieldLocked("website")}>
                            <input
                                type="url"
                                value={website}
                                onChange={(event) => setWebsite(event.target.value)}
                                disabled={isFieldLocked("website")}
                                placeholder="Enter web address"
                                className={`h-[56px] w-full rounded-lg bg-[#f1f5f9] px-4 text-[#101828] outline-none placeholder:text-[#7A80AE] ${
                                    cleanWebsite && !websiteIsValid ? "ring-1 ring-red-500" : ""
                                }`}
                            />
                        </LockedField>
                        {cleanWebsite && !websiteIsValid && (
                            <p className="mt-1 text-sm text-red-600">
                                Enter a valid website address.
                            </p>
                        )}
                    </div>

                    <div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div>
                                <div className="mb-1 flex items-center gap-2">
                                    <p className="aku-body-small-medium text-[#101828]">
                                        Source of funds
                                    </p>
                                    <InfoTooltip
                                        message="Verify the source of your business funds, in line with AML compliance laws"
                                        className="text-[#7A80AE]"
                                    />
                                </div>
                  <LockedField locked={isFieldLocked("sourceOfFunds")}>
                                    <Dropdown
                                        value={sourceOfFunds}
                                        onChange={(value) => {
                                            setSourceOfFunds(value);
                                            setSourceDocumentType("");
                                            setSourceOfFundsDoc(null);
                                        }}
                                        options={SOURCE_OF_FUNDS_OPTIONS}
                                        placeholder="Select source of funds"
                                        className="!mt-0"
                                        dropdownClassName="max-h-56"
                                        readOnly={
                                            hasExistingSourceDocument ||
                                            isFieldLocked("sourceOfFunds")
                                        }
                                    />
                  </LockedField>
                            </div>
                            <div>
                                <p className="mb-1 aku-body-small-medium text-[#101828]">
                                    Document type
                                </p>
                                <LockedField locked={isFieldLocked("sourceDocumentType")}>
                                    <Dropdown
                                        value={sourceDocumentType}
                                        onChange={(value) => {
                                            setSourceDocumentType(value);
                                            setSourceOfFundsDoc(null);
                                        }}
                                        options={sourceDocumentTypeOptions}
                                        placeholder={
                                            sourceOfFunds
                                                ? "Document type"
                                                : "Select source of funds first"
                                        }
                                        className="!mt-0"
                                        dropdownClassName="max-h-56"
                                        readOnly={
                                            hasExistingSourceDocument ||
                                            !sourceOfFunds ||
                                            isFieldLocked("sourceDocumentType")
                                        }
                                    />
                                </LockedField>
                            </div>
                        </div>

                        <div className="mt-6 rounded-xl">
                            <LockedField locked={isFieldLocked("sourceOfFundsDoc")}>
                                <FileUploadTile
                                    label="Upload document"
                                    labelClassName="aku-body-small-medium !mb-1"
                                    initialName={sourceOfFundsDoc?.name}
                                    fileId="usd-source-of-funds-document"
                                    disabled={
                                        hasExistingSourceDocument ||
                                        !sourceDocumentType ||
                                        isFieldLocked("sourceOfFundsDoc")
                                    }
                                    directUpload
                                    onUploaded={({ name, url, fileId, key }) =>
                                        setSourceOfFundsDoc({ name, url, fileId, key })
                                    }
                                    onCleared={() => setSourceOfFundsDoc(null)}
                                />
                            </LockedField>
                        </div>
                        <div className="mt-3 flex items-start gap-2 text-[#475467]">
                            <InfoCircle size="20" color="#2C53F2" variant="Bold" />
                            <p className="text-sm">
                                Your document should clearly show your business name and relevant
                                dates.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-10 flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => (onBack ? onBack() : navigate(-1))}
                        disabled={submitting}
                        className="h-12 rounded-lg px-6 text-[#101828]"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit || submitting}
                        className={`flex h-12 min-w-[120px] items-center justify-center rounded-lg px-6 text-white ${
                            canSubmit && !submitting
                                ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                : "cursor-not-allowed bg-[#D7C5FF]"
                        }`}
                    >
                        {submitting ? <Loader /> : "Submit"}
                    </button>
                </div>
            </div>

            <WalletDetailsModal
                isOpen={Boolean(openWalletCode)}
                currencyCode={openWalletCode}
                initialStep={2}
                onClose={() => setOpenWalletCode(null)}
                onOpenWallet={(data) => {
                    setOpenWalletCode(null);
                    setReadyWallet(data);
                }}
            />

            <WalletReadyModal
                isOpen={Boolean(readyWallet)}
                wallet={readyWallet}
                onClose={() => {
                    setReadyWallet(null);
                    navigate("/wallets");
                }}
            />
        </OnboardingLayout>
    );
}
